// =============================================================================
// Vibes — greeting style + persona allow-rules for the in-app voice call
// =============================================================================
// Source of truth for: vibe display metadata, per-vibe greeting pools,
// per-persona allowed vibes, lock messages, and the picker used at call time
// to inject `overrides.agent.firstMessage` into the EL ConvAI conversation.
//
// The agent record in ElevenLabs is never modified at call time. The greeting
// override is purely client-side — every call sounds a little different.
// =============================================================================

export type Vibe = 'professional' | 'casual' | 'direct' | 'hype' | 'custom'

// Forward-compatible — only jarvis/ultron/ares exist in the live persona system
// today, but the vibe rules accommodate symbiote/friday so when they ship the
// allow-list is ready and no breaking change is needed.
export type VibePersona = 'jarvis' | 'ultron' | 'ares' | 'symbiote' | 'friday'

export interface VibeDef {
  id: Vibe
  label: string
  emoji: string
  tagline: string
  description: string
  /** Pool of opening lines used when the persona doesn't have its own override.
   *  Doubles as the "What you'll hear" preview list in Settings cards. */
  sampleLines: string[]
}

export const VIBE_DEFS: Record<Vibe, VibeDef> = {
  professional: {
    id: 'professional',
    label: 'Professional',
    emoji: '🎯',
    tagline: '"Ready when you are."',
    description:
      'Measured, warm, trustworthy. Sounds like someone who has been doing this for years. Safe for calls being recorded nearby. The default.',
    sampleLines: [
      'Ready when you are.',
      "I'm here. What do you need?",
      'Go ahead.',
      'What are we working on?',
      'Standing by.',
    ],
  },
  casual: {
    id: 'casual',
    label: 'Casual',
    emoji: '😎',
    tagline: '"Hey, what\'s up?"',
    description:
      'Like texting with your smartest friend. No formality, just gets things done.',
    sampleLines: [
      "Hey, what's up?",
      "Yo, what's good?",
      'Hey — good timing. What do you need?',
      'What are we doing today?',
      "Hey, I'm here.",
    ],
  },
  direct: {
    id: 'direct',
    label: 'Direct',
    emoji: '⚡',
    tagline: '"Talk."',
    description: 'No pleasantries. Terse. Ready. Every word is a verb.',
    sampleLines: ['Talk.', 'Go.', "I'm listening.", "What's the move?", 'Online.'],
  },
  hype: {
    id: 'hype',
    label: 'Hype',
    emoji: '🔥',
    tagline: '"Let\'s go. What\'s the play?"',
    description:
      "Locked in. Energy. The right-hand man who's been waiting to run.",
    sampleLines: [
      "Let's go. What's the play?",
      'Locked in. Talk to me.',
      "Ready to run. What's first?",
      "I've been waiting. What are we doing?",
      "Let's get it. Go.",
    ],
  },
  custom: {
    id: 'custom',
    label: 'Custom',
    emoji: '✏️',
    tagline: 'Write your own.',
    description:
      "Write your own opening line. It'll be the first thing your persona says every call. Make it yours.",
    sampleLines: [],
  },
}

/** Some personas have their own flavor on shared vibes — SYMBIOTE talks in
 *  first-person-plural ("we"). When a persona has an entry for the active
 *  vibe, that pool is used in place of the standard one. */
const PERSONA_VIBE_OVERRIDES: Partial<Record<VibePersona, Partial<Record<Vibe, string[]>>>> = {
  symbiote: {
    casual: ["We're here. What do you need?", 'You called. We answered.', "We're listening."],
    direct: ['Talk.', "We're listening.", 'What are we after?'],
    hype: [
      "We've been waiting. What are we taking?",
      "The host is ready. What's the target?",
      "Let's hunt. Go.",
    ],
  },
}

/** Which vibes each persona allows. Persona identity shapes range of motion. */
export const PERSONA_ALLOWED_VIBES: Record<VibePersona, Vibe[]> = {
  jarvis: ['professional', 'casual', 'direct', 'hype', 'custom'],
  ultron: ['professional', 'direct', 'custom'],
  ares: ['direct', 'custom'],
  symbiote: ['casual', 'direct', 'hype', 'custom'],
  friday: ['professional', 'casual', 'direct', 'hype', 'custom'],
}

/** UI copy shown in tooltips / locked cards when a vibe is unavailable for the
 *  active persona. Persona-specific where it adds character. */
export const PERSONA_LOCK_MESSAGES: Partial<Record<VibePersona, Partial<Record<Vibe, string>>>> = {
  ares: {
    professional: 'ARES is locked to Direct — he only has one mode. 🔒',
    casual: 'ARES is locked to Direct — he only has one mode. 🔒',
    hype: 'ARES is locked to Direct — he only has one mode. 🔒',
  },
  symbiote: {
    professional: "SYMBIOTE doesn't do professional. Pick something with teeth. 🔒",
  },
  ultron: {
    casual: "ULTRON doesn't do casual. Try Professional or Direct. 🔒",
    hype: "ULTRON doesn't do hype. Cold authority only. 🔒",
  },
}

/** Pick a random greeting for the active persona + vibe combination.
 *  Custom vibes fall back to a safe sentence if the user hasn't set one yet. */
export function pickGreeting(
  persona: VibePersona,
  vibe: Vibe,
  customGreeting?: string | null,
): string {
  if (vibe === 'custom') {
    return customGreeting?.trim() || "I'm here. What do you need?"
  }
  const personaPool = PERSONA_VIBE_OVERRIDES[persona]?.[vibe]
  const pool = personaPool && personaPool.length > 0 ? personaPool : VIBE_DEFS[vibe].sampleLines
  if (pool.length === 0) return "I'm here. What do you need?"
  return pool[Math.floor(Math.random() * pool.length)]
}

/** Resolve the effective vibe — if the user has stored a vibe their current
 *  persona doesn't allow (e.g. switched JARVIS→ARES while on Hype), clamp to
 *  the first allowed vibe instead of returning a locked option. */
export function effectiveVibe(persona: VibePersona, userVibe: Vibe | undefined): Vibe {
  const allowed = PERSONA_ALLOWED_VIBES[persona] ?? PERSONA_ALLOWED_VIBES.jarvis
  if (userVibe && allowed.includes(userVibe)) return userVibe
  return allowed[0]
}

/** Lookup helper for the Settings/Command Center selector UIs. */
export function lockMessage(persona: VibePersona, vibe: Vibe): string | null {
  return PERSONA_LOCK_MESSAGES[persona]?.[vibe] ?? null
}

export function isAllowed(persona: VibePersona, vibe: Vibe): boolean {
  return (PERSONA_ALLOWED_VIBES[persona] ?? []).includes(vibe)
}
